import React from 'react'
import { CarouselNav, HudsonCarousel } from '../components/ui/carousel-nav'
import { Home, Shield, Clock, Users, Star, Settings } from 'lucide-react'

const CarouselDemoPage: React.FC = () => {
  const serviceCards = [
    {
      icon: <Home className="h-12 w-12 text-white" />,
      title: "Seasonal Home Snapshots",
      description: "Like a wellness checkup for your home — delivered quarterly with detailed reports and proactive recommendations.",
      features: ["Comprehensive inspections", "Detailed photo documentation", "Priority issue identification", "Preventive maintenance planning"]
    },
    {
      icon: <Settings className="h-12 w-12 text-white" />,
      title: "Tailored Maintenance Plans",
      description: "Custom maintenance schedules designed specifically for your home's unique needs and your lifestyle preferences.",
      features: ["Personalized scheduling", "Premium vendor network", "Quality assurance", "Complete coordination"]
    },
    {
      icon: <Shield className="h-12 w-12 text-white" />,
      title: "Emergency Coordination",
      description: "24/7 emergency response coordination with our trusted network of professionals for urgent home issues.",
      features: ["Round-the-clock availability", "Immediate response team", "Insurance coordination", "Priority scheduling"]
    },
    {
      icon: <Users className="h-12 w-12 text-white" />,
      title: "Vendor Management",
      description: "Access to our curated network of premium service providers, all vetted and managed by your Home Director.",
      features: ["Pre-screened professionals", "Quality guarantees", "Competitive pricing", "Seamless scheduling"]
    },
    {
      icon: <Star className="h-12 w-12 text-white" />,
      title: "Concierge Services",
      description: "White-glove concierge services for all your home-related needs, from routine tasks to special projects.",
      features: ["Personal concierge", "Custom requests", "Premium partnerships", "Exclusive access"]
    }
  ];

  const testimonials = [
    {
      quote: "Being part of The Hudson Club is like having an invisible team making our home flawless.",
      author: "Victoria Ashworth",
      location: "Hudson Valley Estate",
      memberSince: "Member Since 2022"
    },
    {
      quote: "They caught a potential foundation issue that could have cost us $50,000. This isn't just service — it's peace of mind.",
      author: "Jonathan Pierce", 
      location: "Westchester Manor",
      memberSince: "Member Since 2021"
    },
    {
      quote: "My Hudson knows our home better than we do. The Complete Home History™ they maintain is invaluable.",
      author: "Catherine Chen",
      location: "Millbrook Residence", 
      memberSince: "Member Since 2023"
    }
  ];

  // Sample carousel items for services
  const serviceItems = serviceCards.map((service, index) => (
    <div key={index} className="p-8 mx-4">
      <div className="bg-white rounded-lg shadow-lg p-8 h-full" style={{ maxWidth: '600px', margin: '0 auto' }}>
        <div 
          className="w-20 h-20 rounded-full flex items-center justify-center mb-6 mx-auto"
          style={{ backgroundColor: 'rgb(82, 76, 61)' }}
        >
          {service.icon}
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">{service.title}</h3>
        <p className="text-gray-600 mb-6 leading-relaxed text-center">{service.description}</p>
        
        <div className="space-y-3">
          {service.features.map((feature, idx) => (
            <div key={idx} className="flex items-center">
              <div className="w-2 h-2 rounded-full mr-3" style={{ backgroundColor: 'rgb(82, 76, 61)' }} />
              <span className="text-gray-700">{feature}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  ));

  // Sample carousel items for testimonials
  const testimonialItems = testimonials.map((testimonial, index) => (
    <div key={index} className="p-8 mx-4">
      <div className="bg-white rounded-lg shadow-lg p-12 h-full text-center" style={{ maxWidth: '700px', margin: '0 auto' }}>
        <div className="mb-8">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="inline-block h-5 w-5 text-yellow-400 fill-current mr-1" />
          ))}
        </div>
        
        <blockquote className="text-2xl font-medium text-gray-900 mb-8 italic leading-relaxed">
          "{testimonial.quote}"
        </blockquote>
        
        <div className="border-t border-gray-200 pt-6">
          <p className="font-semibold text-gray-900 text-lg">{testimonial.author}</p>
          <p className="text-gray-600">{testimonial.location}</p>
          <p className="text-sm text-gray-500 uppercase tracking-wide mt-2">{testimonial.memberSince}</p>
        </div>
      </div>
    </div>
  ));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          
          {/* Page Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Carousel Navigation Component
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Interactive carousel navigation based on Figma design. Features smooth animations, 
              navigation controls, and progress indicators perfect for showcasing services and content.
            </p>
          </div>

          {/* Services Carousel */}
          <div className="mb-20">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Premium Services</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Discover the comprehensive range of services available to Hudson Club members
              </p>
            </div>
            
            <HudsonCarousel 
              items={serviceItems}
              title="Explore Our Services"
              autoPlay={true}
              autoPlayDelay={5000}
            />
          </div>

          {/* Testimonials Carousel */}
          <div className="mb-20">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Member Testimonials</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Hear from our distinguished members about their Hudson Club experience
              </p>
            </div>
            
            <HudsonCarousel 
              items={testimonialItems}
              title="Read Member Stories"
              autoPlay={false}
            />
          </div>

          {/* Stand-alone Navigation Demo */}
          <div className="mb-20">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Navigation Only</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                The standalone carousel navigation component with exact Figma positioning
              </p>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 py-12">
              <CarouselNav
                title="Browse Our Portfolio"
                onPrevious={() => console.log('Previous')}
                onNext={() => console.log('Next')}
                currentIndex={1}
                totalItems={5}
              />
            </div>
          </div>

          {/* Usage Instructions */}
          <div className="bg-white p-8 rounded-lg border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Component Features & Usage
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Key Features:</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Exact Figma design implementation</li>
                  <li>• Smooth framer-motion animations</li>
                  <li>• Responsive navigation controls</li>
                  <li>• Progress indicators</li>
                  <li>• Auto-play functionality</li>
                  <li>• Accessible keyboard navigation</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Design Specs:</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Container: 1200px max width</li>
                  <li>• Colors: rgb(82, 76, 61) accents</li>
                  <li>• Typography: 16px medium weight</li>
                  <li>• Navigation: 30px circular buttons</li>
                  <li>• Divider: Full-width 1px line</li>
                  <li>• Responsive on all devices</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Perfect Use Cases:</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                <div>• Service showcases</div>
                <div>• Member testimonials</div>
                <div>• Property galleries</div>
                <div>• Team introductions</div>
                <div>• Process explanations</div>
                <div>• Feature highlights</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CarouselDemoPage 